import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-triggered animations
 * @param {string} animationClass - CSS class to add when element is in view
 * @param {number} threshold - Intersection threshold (0-1)
 * @param {number} rootMargin - Root margin for intersection observer
 * @param {number} delay - Delay in milliseconds before animation starts
 * @returns {object} - { ref, isVisible, hasAnimated }
 */
const useScrollAnimation = (
  animationClass = 'animate-in',
  threshold = 0.1,
  rootMargin = '0px 0px -50px 0px',
  delay = 0
) => {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            // Add delay if specified
            if (delay > 0) {
              setTimeout(() => {
                setIsVisible(true);
                setHasAnimated(true);
                element.classList.add(animationClass);
              }, delay);
            } else {
              setIsVisible(true);
              setHasAnimated(true);
              element.classList.add(animationClass);
            }
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [animationClass, threshold, rootMargin, delay, hasAnimated]);

  return { ref: elementRef, isVisible, hasAnimated };
};

export default useScrollAnimation;
